import CloseIcon from "@mui/icons-material/Close";
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import searchIcon from "../../assets/images/icons/searchIcon.svg";
import CustomTable from "../../components/customTable";
import useDebounce from "../../hooks/use.debounce";
import { useGetActiveUserSessionsQuery, useGetUserSessionDetailQuery } from "../../redux/api";
import { describeClient } from "../../utils/common";
import { CustomSearchInput, FORM_SELECTFIELD_STYLES } from "../../utils/constant";
import { Card, PageBody, PageHeader } from "./components/SecurityShell";
import { USER_TYPES, compactParams, formatDateTime } from "./securityUtils";

// Override the shared style's width:100% so the select sits inline in the header.
const selectSx = { ...FORM_SELECTFIELD_STYLES, maxHeight: "48px", width: "auto", minWidth: "180px" };

const DeviceRow = ({ session }) => (
    <Box sx={{ py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="body2" fontWeight={700}>{describeClient(session.userAgent)}</Typography>
            <Typography variant="caption" color="text.secondary">
                {formatDateTime(session.lastSeenAt || session.createdDate)}
            </Typography>
        </Stack>
        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">IP: {session.ipAddress || "-"}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                Device: {session.deviceId ? String(session.deviceId).slice(0, 16) + "…" : "-"}
            </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
            Session: {session.sessionId || "-"}
        </Typography>
        <br />
        <Typography variant="caption" color="text.secondary">
            Signed in {formatDateTime(session.createdDate)}
        </Typography>
    </Box>
);

const SessionDrawer = ({ user, open, onClose }) => {
    const { data, isFetching } = useGetUserSessionDetailQuery(user?.userId, {
        skip: !user?.userId || !open,
    });
    const detail = data?.result;
    const sessions = detail?.sessions || [];

    return (
        <Drawer anchor="right" open={open} onClose={onClose}>
            <Box sx={{ width: { xs: "100vw", sm: 460 }, p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700}>Active Devices</Typography>
                    <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
                </Stack>

                {user && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {user.email || (user.phoneNumber ? `Phone: ${user.phoneNumber}` : "No email or phone on file")} · Account ID {user.userId} · {user.userType}
                    </Typography>
                )}

                {isFetching && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={28} /></Stack>}

                {!isFetching && detail && (
                    <>
                        {detail.inSync === false && (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {detail.recordedSessionCount} session record(s), but {detail.liveTokenCount} live
                                token(s) in Redis. Redis is the source of truth for authentication — the difference
                                is sessions whose token expired rather than being explicitly logged out.
                            </Alert>
                        )}

                        {sessions.length === 0 ? (
                            <Alert severity="info">No active sessions for this user.</Alert>
                        ) : (
                            <Stack divider={<Divider />}>
                                {sessions.map((s) => <DeviceRow key={s.sessionId || s.id} session={s} />)}
                            </Stack>
                        )}
                    </>
                )}
            </Box>
        </Drawer>
    );
};

/**
 * Every user currently holding a live session token, with their device count.
 * Read-only: revoking a session is still the user's own action via the Active
 * Devices page.
 */
const ActiveSessions = () => {
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    const [search, setSearch] = useState("");
    const [userType, setUserType] = useState("");
    const [selected, setSelected] = useState(null);

    const debouncedSearch = useDebounce(search, 500);

    const queryFilters = useMemo(
        () => compactParams({ search: debouncedSearch, userType }),
        [debouncedSearch, userType]
    );

    const { data, isFetching } = useGetActiveUserSessionsQuery({
        ...queryFilters,
        limit,
        offset: page * limit,
    });

    const users = data?.result?.data || [];
    const count = data?.result?.count || 0;

    // Column keys are snake_case because CustomTable derives the header from them
    // by splitting on "_" and capitalising each word.
    const cols = ["user", "type", "active_tokens", "devices", "last_seen", "latest_login"];
    const tableData = users.map((u) => ({
        userId: u.userId,
        email: u.email,
        phoneNumber: u.phoneNumber,
        userType: u.userType,
        // Accounts created through phone-OTP signup have no email, so this falls
        // back to the phone number. Label it, otherwise a bare 10-digit string
        // reads as an unexplained number.
        user: (
            <span>
                {u.email
                    ? u.email
                    : u.phoneNumber
                        ? <span>Phone: {u.phoneNumber}</span>
                        : <span style={{ color: "#888" }}>No email or phone on file</span>}
                <br />
                <span style={{ color: "#888", fontSize: 12 }}>Account ID {u.userId}</span>
            </span>
        ),
        type: u.userType || "-",
        active_tokens: (
            <Chip label={u.activeSessions} size="small" color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
        ),
        devices: u.distinctDevices ?? "-",
        last_seen: formatDateTime(u.lastSeenAt),
        latest_login: formatDateTime(u.latestLoginDate),
    }));

    const Action = (row) => (
        <div className="actBtnGrps">
            <span className="viewIcon" onClick={() => setSelected(row)}>View devices</span>
        </div>
    );

    return (
        <>
            <Stack direction="column" gap={2}>
                <PageHeader
                    title="Active Sessions"
                    subtitle="Users with a live token right now, and the devices those tokens are bound to."
                    actions={
                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                            <Select
                                sx={selectSx}
                                displayEmpty
                                value={userType}
                                onChange={(e) => { setUserType(e.target.value); setPage(0); }}
                            >
                                <MenuItem value="">All user types</MenuItem>
                                {USER_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                            <CustomSearchInput
                                className="inputSearch sContrl p-0"
                                value={search}
                                placeholder="Email, phone or user id..."
                                handleSearch={(e) => { setSearch(e.target.value); setPage(0); }}
                                clearSearch={() => { setSearch(""); setPage(0); }}
                                startAdornment={
                                    <InputAdornment className="overIcon" position="start"
                                        sx={{ maxHeight: "none", height: "38px", minWidth: "38px", marginRight: "0px", background: "none !important", borderRadius: "50px !important" }}>
                                        <img src={searchIcon} height={20} width={20} alt="" />
                                    </InputAdornment>
                                }
                            />
                        </Stack>
                    }
                />

                <PageBody>
                    <Card>
                        <CustomTable
                            // Remount on filter change so pagination resets to page 1.
                            key={JSON.stringify(queryFilters)}
                            cols={cols}
                            data={tableData}
                            isCheckbox={false}
                            actionComp={Action}
                            count={count}
                            onPageChange={({ page: p, rows }) => { setPage(p); setLimit(rows); }}
                        />
                        {isFetching && (
                            <Box sx={{ p: 1, textAlign: "center" }}>
                                <Typography variant="caption" color="text.secondary">Loading…</Typography>
                            </Box>
                        )}
                    </Card>

                    <Typography variant="caption" color="text.secondary">
                        Sessions are device-bound: a token copied to another device is rejected. Users can review and
                        revoke their own devices from their Active Devices page.
                    </Typography>
                </PageBody>
            </Stack>

            <SessionDrawer
                user={selected}
                open={!!selected}
                onClose={() => setSelected(null)}
            />
        </>
    );
};

export default ActiveSessions;
